/**
 * Phase 99 (Plan 99-01) — API client for the admin CSV import surface.
 * Types mirror the backend's `csvImportResultResponse` / `csvImportEntitiesResponse`
 * (`backend/src/api/schemas/adminImport.ts`) field-for-field.
 */
import { apiGet, API_BASE_URL, getBaseAuthHeaders, ApiClientError, type ApiError } from './client';

export interface ImportEntity {
  entity: string;
  label: string;
  module: string;
  endpoint: string;
  requiredColumns: string[];
  optionalColumns: string[];
}

export interface CsvImportProblem {
  row: number;
  reason: string;
  detail: string | null;
}

export interface CsvImportResult {
  entity: string;
  imported: number;
  totalRows: number;
  rowsSkipped: number;
  dryRun: boolean;
  problems: CsvImportProblem[];
}

export interface ImportEntitiesResponse {
  data: ImportEntity[];
}

export interface CsvImportResultResponse {
  data: CsvImportResult;
}

export async function fetchImportEntities(): Promise<ImportEntitiesResponse> {
  return apiGet('/admin/imports/entities');
}

export async function uploadImportCsv(
  entity: string,
  file: File,
  dryRun: boolean
): Promise<CsvImportResultResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('dryRun', dryRun ? 'true' : 'false');

  const response = await fetch(`${API_BASE_URL}/admin/imports/${entity}`, {
    method: 'POST',
    credentials: 'include',
    headers: getBaseAuthHeaders(),
    body: formData,
  });

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      statusCode: response.status,
      error: 'Unknown Error',
      message: response.statusText,
    }));
    throw new ApiClientError(error);
  }

  return response.json();
}
